import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import PolaroidPhoto from './PolaroidPhoto';
import MessageCard from './MessageCard';
import type { Photo } from '../types';

interface CollageItem {
  id: string;
  type: 'photo' | 'message';
  photo?: Photo;
  message?: string;
  uploaderName?: string;
  x: number;
  y: number;
  visible: boolean;
  entering: boolean;
  leaving: boolean;
}

function getVisibleCount(): { photos: number; messages: number; isMobile: boolean } {
  if (typeof window === 'undefined') return { photos: 8, messages: 2, isMobile: false };
  const width = window.innerWidth;
  if (width < 640) return { photos: 4, messages: 1, isMobile: true };   // Mobile
  if (width < 1024) return { photos: 6, messages: 2, isMobile: false }; // Tablet
  return { photos: 10, messages: 3, isMobile: false };                  // Desktop
}

// Grid-based positioning for mobile to prevent overlaps
function getMobileGridPosition(index: number): { x: number; y: number } {
  const cols = 2;
  const row = Math.floor(index / cols);
  const col = index % cols;
  
  // Add some randomness within the grid cell
  const cellWidth = 45;
  const cellHeight = 35;
  const baseX = 5 + col * cellWidth;
  const baseY = 5 + row * cellHeight;
  
  return {
    x: baseX + Math.random() * 5,
    y: baseY + Math.random() * 5,
  };
}

function generatePosition(existingPositions: CollageItem[], itemId: string, isMobile: boolean, index: number): { x: number; y: number } {
  if (isMobile) {
    return getMobileGridPosition(index);
  }

  const padding = 10;
  const minDistance = 18;
  
  let attempts = 0;
  let bestPosition = { x: padding, y: padding };
  let bestMinDistance = 0;

  while (attempts < 50) {
    const x = padding + Math.random() * (100 - 2 * padding - 18);
    const y = padding + Math.random() * (100 - 2 * padding - 25);
    
    let minDist = Infinity;
    for (const pos of existingPositions) {
      if (pos.id === itemId || !pos.visible) continue;
      const dist = Math.sqrt(Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2));
      minDist = Math.min(minDist, dist);
    }

    if (minDist > bestMinDistance) {
      bestMinDistance = minDist;
      bestPosition = { x, y };
    }

    if (minDist >= minDistance) {
      return { x, y };
    }

    attempts++;
  }

  return bestPosition;
}

export default function PhotoCollage() {
  const { photos, setSelectedPhotoId } = useApp();
  const [positions, setPositions] = useState<CollageItem[]>([]);
  const [counts, setCounts] = useState(getVisibleCount);

  // Extract messages from photos
  const messagesWithPhotos = useMemo(() => {
    return photos
      .filter(p => p.message && p.message.trim())
      .map(p => ({
        id: `msg-${p.id}`,
        message: p.message!,
        uploaderName: p.uploaderName,
      }));
  }, [photos]);

  // Update counts on resize
  useEffect(() => {
    const handleResize = () => {
      setCounts(getVisibleCount());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize positions when photos/messages change
  useEffect(() => {
    if (photos.length === 0) {
      setPositions([]);
      return;
    }

    setPositions(prev => {
      const newPositions: CollageItem[] = [];
      const photosToShow = photos.slice(0, counts.photos);
      const messagesToShow = messagesWithPhotos.slice(0, counts.messages);
      
      let itemIndex = 0;

      // Add photos
      photosToShow.forEach((photo) => {
        const existing = prev.find(p => p.id === photo.id);
        if (existing) {
          newPositions.push({ ...existing, photo, visible: true });
        } else {
          const pos = generatePosition(newPositions, photo.id, counts.isMobile, itemIndex);
          newPositions.push({
            id: photo.id,
            type: 'photo',
            photo,
            x: pos.x,
            y: pos.y,
            visible: true,
            entering: true,
            leaving: false,
          });
        }
        itemIndex++;
      });

      // Add messages as separate cards
      messagesToShow.forEach((msg) => {
        const existing = prev.find(p => p.id === msg.id);
        if (existing) {
          newPositions.push({ ...existing, visible: true });
        } else {
          const pos = generatePosition(newPositions, msg.id, counts.isMobile, itemIndex);
          newPositions.push({
            id: msg.id,
            type: 'message',
            message: msg.message,
            uploaderName: msg.uploaderName,
            x: pos.x,
            y: pos.y,
            visible: true,
            entering: true,
            leaving: false,
          });
        }
        itemIndex++;
      });

      // Clear entering state after animation
      setTimeout(() => {
        setPositions(curr => 
          curr.map(p => ({ ...p, entering: false }))
        );
      }, 800);

      return newPositions;
    });
  }, [photos, messagesWithPhotos, counts]);

  // Cycling effect - swap out items periodically
  useEffect(() => {
    const totalItems = photos.length + messagesWithPhotos.length;
    const visibleItems = counts.photos + counts.messages;
    
    if (totalItems <= visibleItems) return;

    const cycleItem = () => {
      setPositions(prev => {
        const visiblePositions = prev.filter(p => p.visible && !p.leaving);
        if (visiblePositions.length === 0) return prev;

        // Pick a random visible item to swap out
        const toRemoveIdx = Math.floor(Math.random() * visiblePositions.length);
        const toRemove = visiblePositions[toRemoveIdx];

        // Find items not currently visible
        const visibleIds = new Set(prev.filter(p => p.visible).map(p => p.id));
        
        // Combine available photos and messages
        const availablePhotos = photos.filter(p => !visibleIds.has(p.id));
        const availableMessages = messagesWithPhotos.filter(m => !visibleIds.has(m.id));
        
        // Randomly pick photo or message
        const usePhoto = availableMessages.length === 0 || (availablePhotos.length > 0 && Math.random() > 0.3);
        
        let newItem: CollageItem | null = null;
        const newPos = generatePosition(prev, 'new', counts.isMobile, visiblePositions.length);

        if (usePhoto && availablePhotos.length > 0) {
          const newPhoto = availablePhotos[Math.floor(Math.random() * availablePhotos.length)];
          newItem = {
            id: newPhoto.id,
            type: 'photo',
            photo: newPhoto,
            x: newPos.x,
            y: newPos.y,
            visible: true,
            entering: true,
            leaving: false,
          };
        } else if (availableMessages.length > 0) {
          const newMsg = availableMessages[Math.floor(Math.random() * availableMessages.length)];
          newItem = {
            id: newMsg.id,
            type: 'message',
            message: newMsg.message,
            uploaderName: newMsg.uploaderName,
            x: newPos.x,
            y: newPos.y,
            visible: true,
            entering: true,
            leaving: false,
          };
        }

        if (!newItem) return prev;

        // Mark old item as leaving
        const updated = prev.map(p => 
          p.id === toRemove.id ? { ...p, leaving: true } : p
        );

        // Add new item after a delay
        const itemToAdd = newItem;
        setTimeout(() => {
          setPositions(curr => {
            const filtered = curr.filter(p => p.id !== toRemove.id);
            return [...filtered, itemToAdd];
          });

          setTimeout(() => {
            setPositions(curr => 
              curr.map(p => p.id === itemToAdd.id ? { ...p, entering: false } : p)
            );
          }, 800);
        }, 800);

        return updated;
      });
    };

    const interval = setInterval(cycleItem, 5000 + Math.random() * 3000);
    return () => clearInterval(interval);
  }, [photos, messagesWithPhotos, counts]);

  const handlePhotoClick = useCallback((photoId: string) => {
    setSelectedPhotoId(photoId);
  }, [setSelectedPhotoId]);

  // Empty state
  if (photos.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-24 h-24 mx-auto mb-6 bg-autumn-gold/10 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-autumn-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-display text-autumn-brown mb-2">
            No Photos Yet
          </h2>
          <p className="font-body text-autumn-light-brown">
            Be the first to share a memory!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative overflow-hidden min-h-[60vh]">
      {positions
        .filter(pos => pos.visible)
        .map(pos => (
          <div
            key={pos.id}
            className={`absolute transition-all duration-700 ${
              pos.entering ? 'animate-fade-in' : ''
            } ${
              pos.leaving ? 'opacity-0 scale-95' : 'opacity-100'
            }`}
            style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              width: counts.isMobile 
                ? 'clamp(120px, 40vw, 160px)' 
                : 'clamp(140px, 16vw, 200px)',
              zIndex: pos.entering ? 10 : 1,
            }}
          >
            {pos.type === 'photo' && pos.photo ? (
              <PolaroidPhoto
                photo={pos.photo}
                onClick={() => handlePhotoClick(pos.photo!.id)}
              />
            ) : pos.type === 'message' && pos.message ? (
              <MessageCard
                message={pos.message}
                uploaderName={pos.uploaderName || 'Guest'}
              />
            ) : null}
          </div>
        ))}
    </div>
  );
}
